import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HomeOutlined, ShoppingCartOutlined, UserOutlined, HistoryOutlined,
  TeamOutlined, ShoppingOutlined, WalletOutlined, CreditCardOutlined,
  BarChartOutlined, FileTextOutlined, ProfileOutlined, MenuOutlined,
  MessageOutlined, MoonOutlined, BgColorsOutlined, LockOutlined,
  DownOutlined, PlusOutlined, SearchOutlined, FileExcelOutlined,
  ShopOutlined, FolderOpenOutlined, EyeOutlined, EditOutlined,
  DeleteOutlined, PrinterOutlined, FilterOutlined, ArrowLeftOutlined,
  PercentageOutlined, RiseOutlined, FallOutlined, InfoCircleOutlined,
  AppstoreOutlined, CloudUploadOutlined, FileImageOutlined,
} from "@ant-design/icons";
import "./HarvestShellDemo.css";

const NAV = [
  { sectionKey: "general", sectionDefault: "GENERAL", items: [{ key: "dashboard", icon: <HomeOutlined />, labelDefault: "Dashboard" }] },
  {
    sectionKey: "operations", sectionDefault: "OPERATIONS",
    items: [
      { key: "sales", icon: <ShoppingCartOutlined />, labelDefault: "Sales" },
      { key: "farmers", icon: <UserOutlined />, labelDefault: "Farmers & Merchants" },
      { key: "qaima", icon: <HistoryOutlined />, labelDefault: "Settlement History" },
      { key: "customers", icon: <TeamOutlined />, labelDefault: "Customers" },
      { key: "products", icon: <ShoppingOutlined />, labelDefault: "Products Catalog" },
    ],
  },
  {
    sectionKey: "financeAccounting", sectionDefault: "FINANCE & ACCOUNTING",
    items: [
      { key: "cash", icon: <WalletOutlined />, labelDefault: "Cash Register" },
      { key: "expenses", icon: <CreditCardOutlined />, labelDefault: "Expenses" },
    ],
  },
  {
    sectionKey: "reports", sectionDefault: "REPORTS",
    items: [
      { key: "fin-reports", icon: <BarChartOutlined />, labelDefault: "Financial Reports" },
      { key: "ops-reports", icon: <FileTextOutlined />, labelDefault: "Operational Reports" },
      { key: "statement", icon: <ProfileOutlined />, labelDefault: "Account Statement", accent: true },
    ],
  },
];

const PAGE_META = {
  dashboard: { titleDefault: "Dashboard", crumbs: [["home", "Home"], ["dashboard", "Dashboard"]] },
  sales: { titleDefault: "Sales", crumbs: [["home", "Home"], ["sales", "Sales"]] },
  farmers: { titleDefault: "Farmers & Merchants", crumbs: [["home", "Home"], ["farmers", "Farmers"]] },
  qaima: { titleDefault: "Settlement History", crumbs: [["home", "Home"], ["farmers", "Farmers"], ["settlementHistory", "Settlement History"]] },
  customers: { titleDefault: "Customers", crumbs: [["home", "Home"], ["customers", "Customers"]] },
  products: { titleDefault: "Products Catalog", crumbs: [["home", "Home"], ["products", "Products"]] },
  cash: { titleDefault: "Cash Register", crumbs: [["home", "Home"], ["payments", "Payments"]] },
  expenses: { titleDefault: "Expenses", crumbs: [["home", "Home"], ["expenses", "Expenses"]] },
  "fin-reports": { titleDefault: "Advanced Financial Reports", crumbs: [["home", "Home"], ["reports", "Reports"], ["financialReports", "Financial reports"]] },
  "ops-reports": { titleDefault: "Operational Reports", crumbs: [["home", "Home"], ["reports", "Reports"], ["operationalReports", "Operational reports"]] },
  statement: { titleDefault: "Account Statement", crumbs: [["home", "Home"], ["reports", "Reports"], ["accountStatement", "Account statement"]] },
};

const DEMO_PRODUCTS = [
  { id: 1, key: "okra", defaultValue: "Okra" },
  { id: 2, key: "eggplant", defaultValue: "Eggplant" },
  { id: 3, key: "orange", defaultValue: "Orange" },
  { id: 4, key: "onion", defaultValue: "Onion" },
];

function Btn({ children, variant = "primary", className = "", ...rest }) {
  return (
    <button type="button" className={`hs-btn hs-btn--${variant} ${className}`.trim()} {...rest}>
      {children}
    </button>
  );
}

/** Demo-only form popup (does not affect real Velan UI). */
function DemoModal({ open, title, children, onClose, onOk, okText }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="hs-modal-root" role="dialog" aria-modal="true">
      <button type="button" className="hs-modal-backdrop" aria-label={t("demo.harvest.actions.close", { defaultValue: "Close" })} onClick={onClose} />
      <div className="hs-modal">
        <div className="hs-modal-header">
          <div className="hs-modal-title">
            <span className="hs-modal-title-icon"><PlusOutlined /></span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="hs-modal-close" onClick={onClose} aria-label={t("demo.harvest.actions.close", { defaultValue: "Close" })}>×</button>
        </div>
        <div className="hs-modal-body">{children}</div>
        <div className="hs-modal-footer">
          <button type="button" className="hs-modal-btn hs-modal-btn--cancel" onClick={onClose}>{t("demo.harvest.actions.cancel", { defaultValue: "Cancel" })}</button>
          <button type="button" className="hs-modal-btn hs-modal-btn--ok" onClick={onOk || onClose}>{okText || t("demo.harvest.actions.add", { defaultValue: "Add" })}</button>
        </div>
      </div>
    </div>
  );
}

function DemoField({ label, children }) {
  return (
    <div className="hs-modal-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function StatMini({ label, value, tone = "neutral" }) {
  return (
    <div className={`hs-stat-mini hs-stat-mini--${tone}`}>
      <div className="hs-stat-mini-label">{label}</div>
      <div className="hs-stat-mini-value">{value}</div>
    </div>
  );
}

function StatCard({ icon, label, value, tone = "blue" }) {
  return (
    <div className="hs-stat-card">
      <div className={`hs-stat-card-icon hs-tone-${tone}`}>{icon}</div>
      <div>
        <div className="hs-stat-card-label">{label}</div>
        <div className="hs-stat-card-value">{value}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, hint, action }) {
  return (
    <div className="hs-empty">
      <div className="hs-empty-icon">{icon}</div>
      <div className="hs-empty-title">{title}</div>
      {hint && <div className="hs-empty-hint">{hint}</div>}
      {action}
    </div>
  );
}

function Panel({ title, badge, extra, children, className = "" }) {
  return (
    <section className={`hs-panel ${className}`.trim()}>
      <div className="hs-panel-head">
        <div className="hs-panel-title">
          {title}
          {badge != null && <span className="hs-badge">{badge}</span>}
        </div>
        {extra}
      </div>
      <div className="hs-panel-body">{children}</div>
    </section>
  );
}

function PageSales() {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.sales", { defaultValue: "Sales" })}</span></div>
          <h1 className="hs-h1 hs-h1--blue">{t("demo.harvest.sales.title", { defaultValue: "Sales" })}</h1>
        </div>
        <div className="hs-actions">
          <Btn variant="excel"><FileExcelOutlined /> {t("demo.harvest.actions.exportExcel", { defaultValue: "Excel" })}</Btn>
          <Btn><PlusOutlined /> {t("demo.harvest.actions.newSale", { defaultValue: "New Sale" })}</Btn>
        </div>
      </div>
      <Panel
        title={t("demo.harvest.sales.invoiceList", { defaultValue: "Customer Invoices List" })}
        extra={<div className="hs-search"><SearchOutlined /><input aria-label={t("demo.harvest.actions.search", { defaultValue: "Search" })} placeholder={t("demo.harvest.actions.searchPlaceholder", { defaultValue: "Search..." })} /></div>}
      >
        <table className="hs-table">
          <thead>
            <tr>
              <th>#</th><th>{t("demo.harvest.table.invoiceNumber", { defaultValue: "Invoice No." })}</th><th>{t("demo.harvest.table.date", { defaultValue: "Date" })}</th><th>{t("demo.harvest.table.customer", { defaultValue: "Customer" })}</th><th>{t("demo.harvest.table.paymentType", { defaultValue: "Payment Type" })}</th>
              <th>{t("demo.harvest.table.subtotal", { defaultValue: "Subtotal" })}</th><th>{t("demo.harvest.table.commissionLabor", { defaultValue: "Commission & Labor" })}</th><th>{t("demo.harvest.table.invoiceTotal", { defaultValue: "Invoice Total" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th>
            </tr>
          </thead>
        </table>
        <EmptyState
          icon={<ShoppingCartOutlined />}
          title={t("demo.harvest.sales.emptyTitle", { defaultValue: "No invoices recorded." })}
          hint={t("demo.harvest.sales.emptyHint", { defaultValue: "No sales invoices yet. Click below to create the first invoice." })}
          action={<Btn><PlusOutlined /> {t("demo.harvest.actions.newSale", { defaultValue: "New Sale" })}</Btn>}
        />
      </Panel>
    </div>
  );
}

function PageFarmers({ onNavigate }) {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero hs-page-hero--farmers">
        <div>
          <h1 className="hs-h1 hs-h1--blue"><ShopOutlined /> {t("demo.harvest.farmers.title", { defaultValue: "Farmers & Merchants" })}</h1>
          <p className="hs-subtitle-ku">{t("demo.harvest.farmers.workflow", { defaultValue: "Sales ← Settlement ← Payment — manage every step here" })}</p>
        </div>
        <div className="hs-actions">
          <StatMini label={t("demo.harvest.farmers.totalDebt", { defaultValue: "Total owed to farmers" })} value="0" tone="warn" />
          <Btn><PlusOutlined /> {t("demo.harvest.actions.addFarmer", { defaultValue: "Add New Farmer" })}</Btn>
          <Btn variant="ghost" onClick={() => onNavigate("qaima")}><HistoryOutlined /> {t("demo.harvest.actions.settlementHistory", { defaultValue: "Settlement History" })}</Btn>
        </div>
      </div>
      <div className="hs-steps">
        {[
          [t("demo.harvest.farmers.steps.salesTitle", { defaultValue: "Sales" }), t("demo.harvest.farmers.steps.salesDescription", { defaultValue: "Record sales in POS" })],
          [t("demo.harvest.farmers.steps.settlementTitle", { defaultValue: "Settlement" }), t("demo.harvest.farmers.steps.settlementDescription", { defaultValue: "Print and close loads and expenses" })],
          [t("demo.harvest.farmers.steps.paymentTitle", { defaultValue: "Payment" }), t("demo.harvest.farmers.steps.paymentDescription", { defaultValue: "Pay the remaining farmer balance after settlement" })],
        ].map(([t, d]) => (
          <div key={t} className="hs-step">
            <div className="hs-step-title">{t}</div>
            <div className="hs-step-desc">{d}</div>
          </div>
        ))}
      </div>
      <Panel
        title={<><AppstoreOutlined /> {t("demo.harvest.farmers.title", { defaultValue: "Farmers & Merchants" })}</>}
        extra={<div className="hs-search"><SearchOutlined /><input aria-label={t("demo.harvest.actions.search", { defaultValue: "Search" })} placeholder={t("demo.harvest.farmers.searchPlaceholder", { defaultValue: "Search by name or number..." })} /></div>}
      >
        <table className="hs-table">
          <thead>
            <tr><th>#</th><th>{t("demo.harvest.table.fullName", { defaultValue: "Full Name" })}</th><th>{t("demo.harvest.table.phoneNumber", { defaultValue: "Phone Number" })}</th><th>{t("demo.harvest.table.weOwe", { defaultValue: "We Owe" })}</th><th>{t("demo.harvest.table.settlements", { defaultValue: "Settlements" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th></tr>
          </thead>
        </table>
        <EmptyState
          icon={<FolderOpenOutlined />}
          title={t("demo.harvest.farmers.emptyTitle", { defaultValue: "No farmers or merchants registered." })}
          hint={t("demo.harvest.farmers.emptyHint", { defaultValue: "No farmers registered. Manage your suppliers here." })}
          action={<Btn><PlusOutlined /> {t("demo.harvest.actions.addFarmer", { defaultValue: "Add New Farmer" })}</Btn>}
        />
      </Panel>
    </div>
  );
}

function PageQaima({ onNavigate }) {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.farmers", { defaultValue: "Farmers" })}</span> → <span>{t("demo.harvest.breadcrumbs.settlementHistory", { defaultValue: "Settlement History" })}</span></div>
          <h1 className="hs-h1">{t("demo.harvest.settlements.closedTitle", { defaultValue: "Closed Settlement History" })}</h1>
          <p className="hs-muted">{t("demo.harvest.settlements.subtitle", { defaultValue: "Closed farmer settlement invoices" })}</p>
        </div>
        <Btn variant="ghost" onClick={() => onNavigate("farmers")}><ArrowLeftOutlined /> {t("demo.harvest.farmers.title", { defaultValue: "Farmers & Merchants" })}</Btn>
      </div>
      <div className="hs-filter-card">
        <label>{t("demo.harvest.filters.farmer", { defaultValue: "Farmer" })}<select defaultValue=""><option value="">{t("demo.harvest.filters.all", { defaultValue: "- All -" })}</option></select></label>
        <label>{t("demo.harvest.filters.fromDate", { defaultValue: "From Date" })}<input type="date" /></label>
        <label>{t("demo.harvest.filters.toDate", { defaultValue: "To Date" })}<input type="date" /></label>
        <Btn><SearchOutlined /> {t("demo.harvest.actions.searchPlaceholder", { defaultValue: "Search..." })}</Btn>
      </div>
      <Panel title={t("demo.harvest.settlements.title", { defaultValue: "Settlements" })} badge="0">
        <table className="hs-table">
          <thead>
            <tr>
              <th>{t("demo.harvest.table.invoiceNumber", { defaultValue: "Invoice No." })}</th><th>{t("demo.harvest.table.farmer", { defaultValue: "Farmer" })}</th><th>{t("demo.harvest.table.date", { defaultValue: "Date" })}</th><th>{t("demo.harvest.table.sales", { defaultValue: "Sales" })}</th>
              <th>{t("demo.harvest.table.totalSales", { defaultValue: "Total Sales" })}</th><th>{t("demo.harvest.table.commission", { defaultValue: "Commission" })}</th><th>{t("demo.harvest.table.net", { defaultValue: "Net" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th>
            </tr>
          </thead>
        </table>
        <EmptyState icon={<FileTextOutlined />} title={t("demo.harvest.settlements.empty", { defaultValue: "No settlements found" })} />
      </Panel>
    </div>
  );
}

function PageCustomers() {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.customers", { defaultValue: "Customers" })}</span></div>
          <h1 className="hs-h1 hs-h1--blue"><ShopOutlined /> {t("demo.harvest.customers.title", { defaultValue: "Customers" })}</h1>
          <p className="hs-muted">{t("demo.harvest.customers.subtitle", { defaultValue: "Customers / Manage" })}</p>
        </div>
        <div className="hs-actions">
          <StatMini label={t("demo.harvest.customers.debts", { defaultValue: "Customer debts" })} value="0" tone="danger" />
          <Btn><PlusOutlined /> {t("demo.harvest.actions.addNewCustomer", { defaultValue: "Add New Customer" })}</Btn>
        </div>
      </div>
      <Panel title={t("demo.harvest.customers.title", { defaultValue: "Customers" })} badge={t("demo.harvest.customers.count", { defaultValue: "{{count}} Customers", count: 0 })}>
        <table className="hs-table">
          <thead>
            <tr><th>#</th><th>{t("demo.harvest.table.fullName", { defaultValue: "Full Name" })}</th><th>{t("demo.harvest.table.phoneNumber", { defaultValue: "Phone Number" })}</th><th>{t("demo.harvest.table.creditLimit", { defaultValue: "Credit limit" })}</th><th>{t("demo.harvest.table.owesUs", { defaultValue: "Owes Us (Balance)" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th></tr>
          </thead>
        </table>
        <EmptyState
          icon={<FolderOpenOutlined />}
          title={t("demo.harvest.customers.empty", { defaultValue: "No customers registered." })}
          hint={t("demo.harvest.customers.empty", { defaultValue: "No customers registered." })}
          action={<Btn><PlusOutlined /> {t("demo.harvest.actions.addCustomer", { defaultValue: "Add customer" })}</Btn>}
        />
      </Panel>
    </div>
  );
}

function PageProducts() {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.products", { defaultValue: "Products" })}</span></div>
          <h1 className="hs-h1">{t("demo.harvest.products.title", { defaultValue: "Products Catalog" })}</h1>
          <p className="hs-muted">{t("demo.harvest.products.subtitle", { defaultValue: "List of vegetables and fruits in Alwa" })}</p>
        </div>
        <div className="hs-actions">
          <Btn variant="ghost">{t("demo.harvest.actions.bulkImageUpload", { defaultValue: "Bulk image upload" })}</Btn>
          <Btn variant="ghost">{t("demo.harvest.actions.regenerateImages", { defaultValue: "Regenerate images" })}</Btn>
          <Btn onClick={() => setAddOpen(true)}><PlusOutlined /> {t("demo.harvest.actions.addProduct", { defaultValue: "Add New Product" })}</Btn>
        </div>
      </div>
      <DemoModal open={addOpen} title={t("demo.harvest.products.addTitle", { defaultValue: "Add New Product" })} onClose={() => setAddOpen(false)}>
        <DemoField label={t("demo.harvest.products.nameKurdish", { defaultValue: "Product Name (Kurdish)" })}>
          <input type="text" placeholder={t("demo.harvest.products.nameKurdishPlaceholder", { defaultValue: "Enter the Kurdish product name" })} />
        </DemoField>
        <DemoField label={t("demo.harvest.products.nameArabic", { defaultValue: "Product Name (Arabic)" })}>
          <input type="text" placeholder={t("demo.harvest.products.nameArabicPlaceholder", { defaultValue: "Enter the Arabic product name" })} />
        </DemoField>
        <DemoField label={t("demo.harvest.products.nameEnglish", { defaultValue: "Product Name (English)" })}>
          <input type="text" placeholder={t("demo.harvest.products.nameEnglishPlaceholder", { defaultValue: "Enter the English product name" })} />
        </DemoField>
        <DemoField label={t("demo.harvest.products.minStockKg", { defaultValue: "Min Stock (kg)" })}>
          <input type="number" defaultValue={100} />
        </DemoField>
        <DemoField label={t("demo.harvest.products.image", { defaultValue: "Product image" })}>
          <div className="hs-modal-drop">
            <CloudUploadOutlined className="hs-modal-drop-icon" />
            <span>{t("demo.harvest.products.imageDrop", { defaultValue: "Drag image here or click" })}</span>
          </div>
          <div className="hs-modal-file-hint">
            <FileImageOutlined />
            <span>{t("demo.harvest.products.imageHint", { defaultValue: "PNG, JPEG, or WebP — up to 2 MB" })}</span>
          </div>
        </DemoField>
      </DemoModal>
      <div className="hs-stat-grid">
        <StatCard icon={<ShoppingOutlined />} label={t("demo.harvest.products.totalProducts", { defaultValue: "Total Products" })} value="14" tone="blue" />
        <StatCard icon={<FallOutlined />} label={t("demo.harvest.products.lowStock", { defaultValue: "Low Stock" })} value="14" tone="red" />
        <StatCard icon={<RiseOutlined />} label={t("demo.harvest.products.totalStock", { defaultValue: "Total Stock" })} value={t("demo.harvest.units.kilograms", { defaultValue: "{{count}} kg", count: 0 })} tone="green" />
        <StatCard icon={<AppstoreOutlined />} label={t("demo.harvest.products.noImage", { defaultValue: "No Image" })} value="14" tone="purple" />
      </div>
      <Panel
        title={
          <div className="hs-tabs">
            {[
              t("demo.harvest.filters.allProducts", { defaultValue: "All" }),
              t("demo.harvest.products.lowStock", { defaultValue: "Low Stock" }),
              t("demo.harvest.products.outOfStock", { defaultValue: "Out of Stock" }),
              t("demo.harvest.products.noImage", { defaultValue: "No Image" }),
            ].map((label, i) => (
              <button key={label} type="button" className={`hs-tab${i === 0 ? " is-active" : ""}`}>{label}</button>
            ))}
          </div>
        }
        extra={<div className="hs-search"><SearchOutlined /><input aria-label={t("demo.harvest.actions.search", { defaultValue: "Search" })} placeholder={t("demo.harvest.actions.searchPlaceholder", { defaultValue: "Search..." })} /></div>}
      >
        <table className="hs-table">
          <thead>
            <tr><th>#</th><th>{t("demo.harvest.table.product", { defaultValue: "Product" })}</th><th>{t("demo.harvest.table.stock", { defaultValue: "Stock" })}</th><th>{t("demo.harvest.products.minStockKg", { defaultValue: "Min Stock (kg)" })}</th><th>{t("demo.harvest.table.sales", { defaultValue: "Sales" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th></tr>
          </thead>
          <tbody>
            {DEMO_PRODUCTS.map((p, i) => (
              <tr key={p.id} className="hs-row-warn">
                <td>{i + 1}</td>
                <td>
                  <div className="hs-product-cell">
                    <div className="hs-product-thumb" />
                    <div>
                      <b>{t(`demo.harvest.productNames.${p.key}`, { defaultValue: p.defaultValue })}</b>
                      <div className="hs-muted">{t(`demo.harvest.productNames.${p.key}`, { defaultValue: p.defaultValue })}</div>
                    </div>
                  </div>
                </td>
                <td>{t("demo.harvest.units.kilograms", { defaultValue: "{{count}} kg", count: 0 })} <span className="hs-chip hs-chip--danger">{t("demo.harvest.status.empty", { defaultValue: "Empty" })}</span></td>
                <td>{t("demo.harvest.units.kilograms", { defaultValue: "{{count}} kg", count: 100 })}</td>
                <td>—</td>
                <td className="hs-row-actions">
                  <button type="button" title={t("demo.harvest.actions.view", { defaultValue: "View" })} aria-label={t("demo.harvest.actions.viewProduct", { defaultValue: "View product" })}><EyeOutlined /></button>
                  <button type="button" title={t("demo.harvest.actions.edit", { defaultValue: "Edit" })} aria-label={t("demo.harvest.actions.editProduct", { defaultValue: "Edit product" })}><EditOutlined /></button>
                  <button type="button" title={t("demo.harvest.actions.delete", { defaultValue: "Delete" })} aria-label={t("demo.harvest.actions.deleteProduct", { defaultValue: "Delete product" })}><DeleteOutlined /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function PageCash() {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.payments", { defaultValue: "Payments" })}</span></div>
          <h1 className="hs-h1">{t("demo.harvest.cash.title", { defaultValue: "Cash Register" })}</h1>
          <p className="hs-muted">{t("demo.harvest.cash.subtitle", { defaultValue: "Record payments to farmers or collections from customers." })}</p>
        </div>
        <div className="hs-actions">
          <Btn variant="excel"><FileExcelOutlined /> {t("demo.harvest.actions.exportExcel", { defaultValue: "Excel" })}</Btn>
          <Btn><PlusOutlined /> {t("demo.harvest.actions.recordPayment", { defaultValue: "Record Payment / Cash-In" })}</Btn>
        </div>
      </div>
      <div className="hs-stat-grid">
        <StatCard icon={<FallOutlined />} label={t("demo.harvest.cash.received", { defaultValue: "RECEIVED" })} value="0" tone="green" />
        <StatCard icon={<RiseOutlined />} label={t("demo.harvest.cash.paidOut", { defaultValue: "PAID OUT" })} value="0" tone="red" />
        <StatCard icon={<WalletOutlined />} label={t("demo.harvest.cash.safes", { defaultValue: "CASH SAFES" })} value="0" tone="purple" />
        <StatCard icon={<BankIcon />} label={t("demo.harvest.cash.cashTransfer", { defaultValue: "CASH / TRANSFER (HAWALA)" })} value="0 / 0" tone="orange" />
      </div>
      <Panel title={t("demo.harvest.cash.logs", { defaultValue: "Main Cashier Register Logs" })} badge={t("demo.harvest.common.recordCount", { defaultValue: "{{count}} records", count: 0 })}>
        <table className="hs-table">
          <thead>
            <tr><th>#</th><th>{t("demo.harvest.table.transactionType", { defaultValue: "Transaction Type" })}</th><th>{t("demo.harvest.table.fullName", { defaultValue: "Full Name" })}</th><th>{t("demo.harvest.table.phoneNumber", { defaultValue: "Phone Number" })}</th><th>{t("demo.harvest.table.balance", { defaultValue: "Balance" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th></tr>
          </thead>
        </table>
        <EmptyState icon={<WalletOutlined />} title={t("demo.harvest.cash.empty", { defaultValue: "No open receivables or payables." })} />
      </Panel>
      <Panel title={t("demo.harvest.cash.history", { defaultValue: "Payment & collection history" })} badge={t("demo.harvest.common.recordCount", { defaultValue: "{{count}} records", count: 0 })} className="hs-mt">
        <table className="hs-table">
          <thead>
            <tr>
              <th>#</th><th>{t("demo.harvest.table.date", { defaultValue: "Date" })}</th><th>{t("demo.harvest.table.transactionType", { defaultValue: "Transaction Type" })}</th><th>{t("demo.harvest.table.relatedParty", { defaultValue: "Related Party (Farmer / Customer)" })}</th>
              <th>{t("demo.harvest.table.amount", { defaultValue: "Amount" })}</th><th>{t("demo.harvest.table.paymentMethod", { defaultValue: "Payment Method" })}</th><th>{t("demo.harvest.table.notes", { defaultValue: "Notes" })}</th><th>{t("demo.harvest.table.actions", { defaultValue: "Actions" })}</th>
            </tr>
          </thead>
        </table>
      </Panel>
    </div>
  );
}

function BankIcon() {
  return <CreditCardOutlined />;
}

function PageExpenses() {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.expenses", { defaultValue: "Expenses" })}</span></div>
          <h1 className="hs-h1">{t("demo.harvest.expenses.title", { defaultValue: "Expenses" })}</h1>
          <p className="hs-muted">{t("demo.harvest.expenses.subtitle", { defaultValue: "General store expenses (Alwa)" })}</p>
        </div>
        <div className="hs-actions">
          <Btn variant="excel"><FileExcelOutlined /> {t("demo.harvest.actions.exportExcel", { defaultValue: "Excel" })}</Btn>
          <Btn onClick={() => setAddOpen(true)}><PlusOutlined /> {t("demo.harvest.actions.addExpense", { defaultValue: "Add New Expense" })}</Btn>
        </div>
      </div>
      <DemoModal open={addOpen} title={t("demo.harvest.expenses.addTitle", { defaultValue: "Add New Expense" })} onClose={() => setAddOpen(false)}>
        <div className="hs-modal-hint">
          <span className="hs-modal-hint-i">i</span>
          <span>
            {t("demo.harvest.expenses.formHint", { defaultValue: "Store expenses — electricity, supplies, rent, salaries, and more. Select a category and amount." })}
          </span>
        </div>
        <DemoField label={t("demo.harvest.expenses.category", { defaultValue: "Category" })}>
          <select defaultValue="electricity">
            <option value="electricity">{t("demo.harvest.expenses.categories.electricity", { defaultValue: "Electricity Bill" })}</option>
            <option value="rent">{t("demo.harvest.expenses.categories.rent", { defaultValue: "Rent" })}</option>
            <option value="salary">{t("demo.harvest.expenses.categories.salaries", { defaultValue: "Salaries" })}</option>
            <option value="other">{t("demo.harvest.expenses.categories.other", { defaultValue: "Other" })}</option>
          </select>
        </DemoField>
        <DemoField label={t("demo.harvest.expenses.amountIqd", { defaultValue: "Amount (IQD)" })}>
          <input type="number" defaultValue={0} />
        </DemoField>
        <DemoField label={t("demo.harvest.expenses.description", { defaultValue: "Description" })}>
          <textarea rows={4} placeholder={t("demo.harvest.expenses.descriptionPlaceholder", { defaultValue: "Enter expense details" })} />
        </DemoField>
      </DemoModal>
      <div className="hs-stat-grid">
        <StatCard icon={<FallOutlined />} label={t("demo.harvest.expenses.total", { defaultValue: "Total Expenses" })} value="0" tone="red" />
        <StatCard icon={<RiseOutlined />} label={t("demo.harvest.expenses.today", { defaultValue: "Today's Expenses" })} value="0" tone="orange" />
        <StatCard icon={<AppstoreOutlined />} label={t("demo.harvest.expenses.categoriesLabel", { defaultValue: "Categories" })} value="0" tone="blue" />
        <StatCard icon={<BarChartOutlined />} label={t("demo.harvest.expenses.largestCategory", { defaultValue: "Largest Category" })} value="—" tone="green" />
      </div>
      <div className="hs-split">
        <Panel title={t("demo.harvest.expenses.list", { defaultValue: "Store Expenses List" })} className="hs-grow">
          <table className="hs-table">
            <thead>
              <tr><th>#</th><th>{t("demo.harvest.table.date", { defaultValue: "Date" })}</th><th>{t("demo.harvest.table.category", { defaultValue: "Category" })}</th><th>{t("demo.harvest.table.amount", { defaultValue: "Amount" })}</th><th>{t("demo.harvest.table.description", { defaultValue: "Description" })}</th></tr>
            </thead>
          </table>
          <EmptyState icon={<FileTextOutlined />} title={t("demo.harvest.expenses.empty", { defaultValue: "No expenses recorded" })} />
        </Panel>
        <Panel title={t("demo.harvest.expenses.distribution", { defaultValue: "Expense Distribution" })} className="hs-side">
          <EmptyState icon={<PieIcon />} title={t("demo.harvest.expenses.emptyPunctuated", { defaultValue: "No expenses recorded." })} />
        </Panel>
      </div>
    </div>
  );
}

function PieIcon() {
  return <PercentageOutlined />;
}

function PageFinReports() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("pl");
  return (
    <div className="hs-page">
      <div className="hs-page-hero hs-page-hero--report">
        <div className="hs-report-title">
          <div className="hs-report-icon"><BarChartOutlined /></div>
          <div>
            <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.reports", { defaultValue: "Reports" })}</span> → <span>{t("demo.harvest.breadcrumbs.financialReports", { defaultValue: "Financial reports" })}</span></div>
            <h1 className="hs-h1">{t("demo.harvest.finReports.title", { defaultValue: "Advanced Financial Reports" })}</h1>
            <p className="hs-muted">{t("demo.harvest.finReports.subtitle", { defaultValue: "Profit, trial balance, debts, payables, and balance sheet in one workspace." })}</p>
          </div>
        </div>
        <Btn><PrinterOutlined /> {t("demo.harvest.actions.print", { defaultValue: "Print" })}</Btn>
      </div>
      <div className="hs-tabs hs-tabs--pills">
        {[
          ["pl", t("demo.harvest.finReports.profitLoss", { defaultValue: "Profit & Loss" })],
          ["tb", t("demo.harvest.finReports.trialBalance", { defaultValue: "Trial Balance" })],
          ["ar", t("demo.harvest.finReports.customerReceivables", { defaultValue: "Customer Receivables" })],
          ["ap", t("demo.harvest.finReports.farmerPayables", { defaultValue: "Farmer Payables" })],
          ["bs", t("demo.harvest.finReports.balanceSheet", { defaultValue: "Balance Sheet" })],
        ].map(([k, label]) => (
          <button key={k} type="button" className={`hs-tab${tab === k ? " is-active" : ""}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "pl" && (
        <div className="hs-stat-grid hs-stat-grid--3">
          <StatCard icon={<RiseOutlined />} label={t("demo.harvest.finReports.commissionRevenue", { defaultValue: "Total Commission Revenue" })} value={t("demo.harvest.units.iqd", { defaultValue: "{{amount}} IQD", amount: 0 })} tone="green" />
          <StatCard icon={<FallOutlined />} label={t("demo.harvest.finReports.operatingExpenses", { defaultValue: "Total Operating Expenses" })} value={t("demo.harvest.units.iqd", { defaultValue: "{{amount}} IQD", amount: 0 })} tone="red" />
          <StatCard icon={<InfoCircleOutlined />} label={t("demo.harvest.finReports.netProfitLoss", { defaultValue: "Net Profit / Loss" })} value={t("demo.harvest.units.iqd", { defaultValue: "{{amount}} IQD", amount: 0 })} tone="blue" />
        </div>
      )}
      {tab !== "pl" && (
        <Panel title={tab.toUpperCase()}>
          <EmptyState icon={<BarChartOutlined />} title={t("demo.harvest.finReports.selectPeriod", { defaultValue: "Select a period to load this report." })} />
        </Panel>
      )}
    </div>
  );
}

function PageOpsReports() {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <h1 className="hs-h1">{t("demo.harvest.opsReports.title", { defaultValue: "Operational Reports" })}</h1>
          <p className="hs-muted">{t("demo.harvest.opsReports.subtitle", { defaultValue: "Sales, loads, debt, stock, and price history in one place." })}</p>
        </div>
        <Btn><PrinterOutlined /> {t("demo.harvest.actions.print", { defaultValue: "Print" })}</Btn>
      </div>
      <div className="hs-filter-card">
        <label>{t("demo.harvest.filters.from", { defaultValue: "From" })}<input type="date" /></label>
        <label>{t("demo.harvest.filters.to", { defaultValue: "To" })}<input type="date" /></label>
        <label>{t("demo.harvest.filters.branch", { defaultValue: "Branch" })}<select defaultValue="all"><option value="all">{t("demo.harvest.filters.allBranches", { defaultValue: "All Branches" })}</option></select></label>
        <Btn><FilterOutlined /> {t("demo.harvest.actions.filter", { defaultValue: "Filter" })}</Btn>
        <Btn variant="ghost"><PrinterOutlined /> {t("demo.harvest.actions.print", { defaultValue: "Print" })}</Btn>
        <Btn variant="excel">{t("demo.harvest.actions.exportCsv", { defaultValue: "CSV" })}</Btn>
      </div>
      <div className="hs-stat-grid">
        <StatCard icon={<FileTextOutlined />} label={t("demo.harvest.opsReports.sales", { defaultValue: "SALES" })} value="0" tone="blue" />
        <StatCard icon={<PercentageOutlined />} label={t("demo.harvest.opsReports.commission", { defaultValue: "COMMISSION" })} value="0" tone="purple" />
        <StatCard icon={<ShopOutlined />} label={t("demo.harvest.opsReports.customerDebt", { defaultValue: "CUSTOMER DEBT" })} value="0" tone="orange" />
        <StatCard icon={<UserOutlined />} label={t("demo.harvest.opsReports.farmerPayable", { defaultValue: "FARMER PAYABLE" })} value="0" tone="red" />
      </div>
      <Panel title={t("demo.harvest.opsReports.dailySales", { defaultValue: "Daily Sales Report" })}>
        <table className="hs-table">
          <thead>
            <tr><th>{t("demo.harvest.table.date", { defaultValue: "Date" })}</th><th>{t("demo.harvest.table.invoices", { defaultValue: "Invoices" })}</th><th>{t("demo.harvest.table.cash", { defaultValue: "Cash" })}</th><th>{t("demo.harvest.table.credit", { defaultValue: "Credit" })}</th><th>{t("demo.harvest.table.total", { defaultValue: "Total" })}</th><th>{t("demo.harvest.table.commissionProfit", { defaultValue: "Commission Profit" })}</th></tr>
          </thead>
        </table>
      </Panel>
      <Panel title={t("demo.harvest.opsReports.loadSummary", { defaultValue: "Load Summary Report" })} className="hs-mt">
        <table className="hs-table">
          <thead>
            <tr>
              <th>{t("demo.harvest.table.load", { defaultValue: "Load" })}</th><th>{t("demo.harvest.table.branch", { defaultValue: "Branch" })}</th><th>{t("demo.harvest.table.farmer", { defaultValue: "Farmer" })}</th><th>{t("demo.harvest.table.productsCatalog", { defaultValue: "Products Catalog" })}</th>
              <th>{t("demo.harvest.table.netKg", { defaultValue: "Net Kg" })}</th><th>{t("demo.harvest.table.revenue", { defaultValue: "Revenue" })}</th><th>{t("demo.harvest.table.farmerNet", { defaultValue: "Farmer Net" })}</th><th>{t("demo.harvest.table.status", { defaultValue: "Status" })}</th><th>{t("demo.harvest.table.settled", { defaultValue: "Settled" })}</th>
            </tr>
          </thead>
        </table>
      </Panel>
    </div>
  );
}

function PageStatement() {
  const { t } = useTranslation();
  return (
    <div className="hs-page">
      <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.reports", { defaultValue: "Reports" })}</span> → <span>{t("demo.harvest.breadcrumbs.accountStatement", { defaultValue: "Account statement" })}</span></div>
      <Panel title={<><FileTextOutlined /> {t("demo.harvest.statement.title", { defaultValue: "Customer / Farmer Account Statement" })}</>}>
        <div className="hs-form-grid">
          <label>{t("demo.harvest.statement.accountType", { defaultValue: "Account Type" })}
            <select defaultValue="farmer"><option value="farmer">{t("demo.harvest.statement.farmerSupplier", { defaultValue: "Farmer / Supplier" })}</option><option value="customer">{t("demo.harvest.statement.customer", { defaultValue: "Customer" })}</option></select>
          </label>
          <label>{t("demo.harvest.statement.selectFarmer", { defaultValue: "-- Select Farmer --" })}
            <select defaultValue=""><option value="">{t("demo.harvest.statement.selectFarmer", { defaultValue: "-- Select Farmer --" })}</option></select>
          </label>
          <label>{t("demo.harvest.filters.fromDate", { defaultValue: "From Date" })}<input type="date" /></label>
          <label>{t("demo.harvest.filters.toDate", { defaultValue: "To Date" })}<input type="date" /></label>
        </div>
        <div className="hs-form-actions">
          <Btn><FilterOutlined /> {t("demo.harvest.actions.generateStatement", { defaultValue: "Generate Statement" })}</Btn>
        </div>
      </Panel>
    </div>
  );
}

function PageDashboard() {
  const { t } = useTranslation();
  const metrics = [
    [t("demo.harvest.dashboard.totalSales", { defaultValue: "Total Sales" }), "—", "blue"],
    [t("demo.harvest.dashboard.cashOnHand", { defaultValue: "Cash on Hand" }), "—", "orange"],
    [t("demo.harvest.dashboard.taxesCommission", { defaultValue: "Taxes (Commission)" }), "—", "purple"],
    [t("demo.harvest.dashboard.warehouseExpenses", { defaultValue: "Warehouse Expenses" }), "—", "red"],
    [t("demo.harvest.dashboard.customerDebts", { defaultValue: "Customer Debts" }), "—", "green"],
    [t("demo.harvest.dashboard.farmerOutstanding", { defaultValue: "Farmer Outstanding" }), "—", "red"],
    [t("demo.harvest.dashboard.netProfit", { defaultValue: "Net Profit" }), "—", "purple"],
    [t("demo.harvest.dashboard.returns", { defaultValue: "Returns" }), "—", "red"],
    [t("demo.harvest.dashboard.soldByWeight", { defaultValue: "Sold by Weight" }), "—", "green"],
    [t("demo.harvest.dashboard.farmerCharges", { defaultValue: "Farmer Charges" }), "—", "orange"],
    [t("demo.harvest.dashboard.costOfGoodsSold", { defaultValue: "Cost of Goods Sold" }), "—", "red"],
  ];
  return (
    <div className="hs-page">
      <div className="hs-page-hero">
        <div>
          <div className="hs-crumbs"><span>{t("demo.harvest.breadcrumbs.home", { defaultValue: "Home" })}</span> → <span>{t("demo.harvest.breadcrumbs.dashboard", { defaultValue: "Dashboard" })}</span></div>
          <h1 className="hs-h1">{t("demo.harvest.dashboard.title", { defaultValue: "Dashboard" })}</h1>
          <p className="hs-muted">{t("demo.harvest.dashboard.subtitle", { defaultValue: "Accountant overview — labels match Harvest KPIs (demo, no live data)." })}</p>
        </div>
      </div>
      <div className="hs-stat-grid">
        {metrics.map(([label, value, tone]) => (
          <StatCard key={label} icon={<BarChartOutlined />} label={label} value={value} tone={tone} />
        ))}
      </div>
    </div>
  );
}

const PAGES = {
  dashboard: PageDashboard,
  sales: PageSales,
  farmers: PageFarmers,
  qaima: PageQaima,
  customers: PageCustomers,
  products: PageProducts,
  cash: PageCash,
  expenses: PageExpenses,
  "fin-reports": PageFinReports,
  "ops-reports": PageOpsReports,
  statement: PageStatement,
};

/**
 * Full Harvest ERP shell recreation — click sidebar to preview each screen.
 * Open at /harvest-demo
 */
const HarvestShellDemo = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [lang, setLang] = useState("EN");
  const [fontSize, setFontSize] = useState("md");
  const [active, setActive] = useState("sales");

  const meta = PAGE_META[active] || PAGE_META.dashboard;
  const Page = useMemo(() => PAGES[active] || PageDashboard, [active]);

  return (
    <div className={`harvest-shell${collapsed ? " harvest-shell--collapsed" : ""}`}>
      <aside className="harvest-sidebar" aria-label={t("demo.harvest.accessibility.mainNavigation", { defaultValue: "Main navigation" })}>
        <div className="harvest-brand">
          <div className="harvest-brand-mark" aria-hidden />
          {!collapsed && (
            <div className="harvest-brand-text">
              <div className="harvest-brand-ku">{t("demo.harvest.brand.kurdishName", { defaultValue: "Harvest Market" })}</div>
              <div className="harvest-brand-en">{t("demo.harvest.brand.name", { defaultValue: "Harvest" })}</div>
            </div>
          )}
        </div>
        <nav className="harvest-nav">
          {NAV.map((group) => (
            <div key={group.sectionKey} className="harvest-nav-group">
              {!collapsed && <div className="harvest-nav-section">{t(`demo.harvest.navigation.sections.${group.sectionKey}`, { defaultValue: group.sectionDefault })}</div>}
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`harvest-nav-item${active === item.key ? " is-active" : ""}${item.accent ? " is-accent" : ""}`}
                  onClick={() => setActive(item.key)}
                  title={t(`demo.harvest.navigation.items.${item.key}`, { defaultValue: item.labelDefault })}
                >
                  <span className="harvest-nav-icon">{item.icon}</span>
                  {!collapsed && <span className="harvest-nav-label">{t(`demo.harvest.navigation.items.${item.key}`, { defaultValue: item.labelDefault })}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="harvest-main">
        <header className="harvest-topbar">
          <div className="harvest-topbar-left">
            <button type="button" className="harvest-icon-btn" onClick={() => setCollapsed((v) => !v)} aria-label={t("demo.harvest.accessibility.toggleSidebar", { defaultValue: "Toggle sidebar" })}>
              <MenuOutlined />
            </button>
            <div className="harvest-page-meta">
              <div className="harvest-page-title-row">
                <span className="harvest-page-title">{t(`demo.harvest.pageMeta.${active}.title`, { defaultValue: meta.titleDefault })}</span>
                <span className="harvest-page-date">{t("demo.harvest.header.dateBrand", { defaultValue: "{{date}} - Harvest", date: "2024-07-14" })}</span>
              </div>
              <nav className="harvest-breadcrumb" aria-label={t("demo.harvest.accessibility.breadcrumb", { defaultValue: "Breadcrumb" })}>
                {meta.crumbs.map(([key, defaultValue], i) => (
                  <React.Fragment key={`${key}-${i}`}>
                    {i > 0 && <span className="harvest-bc-sep">→</span>}
                    <span className={i === meta.crumbs.length - 1 ? "harvest-bc-current" : ""}>{t(`demo.harvest.breadcrumbs.${key}`, { defaultValue })}</span>
                  </React.Fragment>
                ))}
              </nav>
            </div>
          </div>

          <div className="harvest-topbar-right">
            <div className="harvest-font-size" role="group" aria-label={t("demo.harvest.accessibility.fontSize", { defaultValue: "Font size" })}>
              {[["sm", "A-"], ["md", "A"], ["lg", "A+"]].map(([k, label]) => (
                <button key={k} type="button" className={fontSize === k ? "is-active" : ""} onClick={() => setFontSize(k)}>{label}</button>
              ))}
            </div>
            <button type="button" className="harvest-icon-btn" aria-label={t("demo.harvest.accessibility.messages", { defaultValue: "Messages" })}><MessageOutlined /></button>
            <button type="button" className="harvest-icon-btn" aria-label={t("demo.harvest.accessibility.darkMode", { defaultValue: "Dark mode" })}><MoonOutlined /></button>
            <button type="button" className="harvest-icon-btn" aria-label={t("demo.harvest.accessibility.colors", { defaultValue: "Colors" })}><BgColorsOutlined /></button>
            <button type="button" className="harvest-icon-btn" aria-label={t("demo.harvest.accessibility.lock", { defaultValue: "Lock" })}><LockOutlined /></button>
            <div className="harvest-lang" role="group" aria-label={t("demo.harvest.accessibility.language", { defaultValue: "Language" })}>
              {[
                ["Kurdish", t("demo.harvest.languages.kurdish", { defaultValue: "Kurdish" })],
                ["Arabic", t("demo.harvest.languages.arabic", { defaultValue: "Arabic" })],
                ["EN", t("demo.harvest.languages.englishShort", { defaultValue: "EN" })],
              ].map(([value, label]) => (
                <button key={value} type="button" className={lang === value ? "is-active" : ""} onClick={() => setLang(value)}>{label}</button>
              ))}
            </div>
            <button type="button" className="harvest-user">
              <span className="harvest-avatar">M</span>
              <span className="harvest-user-email">manager@Harvest.com</span>
              <DownOutlined className="harvest-user-caret" />
            </button>
          </div>
        </header>

        <main className={`harvest-content harvest-content--scroll harvest-content--font-${fontSize}`}>
          <Page onNavigate={setActive} />
        </main>
      </div>
    </div>
  );
};

export default HarvestShellDemo;
